import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Grid,
  Typography,
  Paper,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { LOGIN } from '@shared/helpers/routes';
import authService from '@shared/services/authService';
import StyledTextField from '@shared/components/StyledTextField';
import { sxStyle } from './styles';

// Alert afuera del componente
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const RecoverPassword = () => {
  const theme = useTheme();
  const history = useHistory();
  const pageConfiguration = useSelector(store => store.pageConfiguration);
  const { token } = useParams();

  const [showPassword, setShowPassword] = React.useState(false);
  const [openAlert, setOpenAlert] = React.useState({
    message: '',
    isVisible: false,
    type: 'error',
  });
  const [isLoading, setIsLoading] = React.useState(false);

  const handleOpenAlert = (message, type) => {
    setOpenAlert({ message, isVisible: true, type });
  };

  const handleCloseAlert = () => {
    setIsLoading(false);
    setOpenAlert({ message: '', isVisible: false, type: 'error' });
  };

  // Schema de validación condicional según token
  const validationSchema = Yup.object().shape({
    email: token
      ? Yup.string()
      : Yup.string().email('Email inválido').required('Debe ingresar un email'),
    password: token
      ? Yup.string().required('Debe ingresar una contraseña')
      : Yup.string(),
  });

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = values => {
    const data = token
      ? { password: values.password }
      : { email: values.email };
    const successMessage = token
      ? 'Contraseña establecida exitosamente!'
      : 'Mail enviado!';

    setIsLoading(true);
    authService
      .recoverPassword(token, data)
      .then(() => {
        handleOpenAlert(successMessage, 'success');
        setTimeout(() => {
          setIsLoading(false);
          history.push(LOGIN);
        }, 3000);
      })
      .catch(error => {
        handleOpenAlert(error.response?.data?.message || 'Error', 'error');
      });
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={sxStyle.root}
    >
      <Paper elevation={1} sx={sxStyle.container}>
        <Grid
          item
          container
          direction="column"
          spacing={2}
          sx={sxStyle.formContainer}
        >
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            sx={sxStyle.header}
          >
            <Grid item container direction="column" xs={9} md={10}>
              <Typography variant="h5" sx={sxStyle.title}>
                {token ? 'Configurar' : 'Recuperar'} contraseña
              </Typography>
              <Typography variant="subtitle1" sx={sxStyle.subtitle}>
                {token
                  ? 'Ingrese su nueva contraseña para poder loguearse'
                  : 'Ingrese su mail para recuperar la contraseña'}
              </Typography>
            </Grid>
            <Grid item xs>
              <Link to={'/'}>
                <img
                  src={pageConfiguration.site_logos.principal}
                  style={{ width: '100%' }}
                />
              </Link>
            </Grid>
          </Grid>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {formik => (
              <Form>
                <Grid item>
                  {!token && (
                    <StyledTextField
                      formik={formik}
                      name="email"
                      label="Email"
                      type="email"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <PersonOutlineIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {token && (
                    <StyledTextField
                      formik={formik}
                      name="password"
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Grid>

                {!token && (
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Se enviará un link a la dirección de mail ingresada para
                    recuperar su contraseña
                  </Typography>
                )}

                <Button
                  sx={{
                    mt: 2,
                    backgroundColor: theme.primary,
                    color: theme.text1,
                    '&:hover': { backgroundColor: alpha(theme.primary, 0.8) },
                  }}
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={25} sx={{ color: theme.text1 }} />
                  ) : (
                    'Enviar'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Grid>
      </Paper>

      <Snackbar
        open={openAlert.isVisible}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={openAlert.type || 'info'}>
          {openAlert.message || ''}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default RecoverPassword;
